/**
 * Admin API client — separate from the public api.js because every call
 * carries the admin JWT in the Authorization header.
 *
 * Token is persisted in localStorage under `lexiq_admin_token`. Cleared on
 * 401 from any endpoint (forces re-login).
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "lexiq_admin_token";

export const getAdminToken = () => localStorage.getItem(TOKEN_KEY) || "";
export const setAdminToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearAdminToken = () => localStorage.removeItem(TOKEN_KEY);

export const adminApi = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

adminApi.interceptors.request.use((config) => {
  const t = getAdminToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

adminApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) clearAdminToken();
    return Promise.reject(err);
  },
);

// ---------- Auth ----------
export const adminLogin = (username, password) =>
  adminApi.post("/admin/login", { username, password }).then((r) => r.data);

export const adminLogout = () =>
  adminApi.post("/admin/logout").then((r) => r.data).catch(() => null);

export const adminMe = () => adminApi.get("/admin/me").then((r) => r.data);

// ---------- Stats ----------
export const adminStats = () => adminApi.get("/admin/stats").then((r) => r.data);

// ---------- Financials (MRR + tier distribution + churn) ----------
export const adminFinancials = () =>
  adminApi.get("/admin/subscriptions/financials").then((r) => r.data);

// ---------- Monitoring (live dashboard) ----------
export const monitoringOverview = () =>
  adminApi.get("/admin/monitoring/overview").then((r) => r.data);

export const monitoringUsers = ({ q = "", online_only = false, limit = 100, skip = 0 } = {}) =>
  adminApi
    .get("/admin/monitoring/users", { params: { q, online_only, limit, skip } })
    .then((r) => r.data);

export const monitoringRecentActivity = (limit = 50) =>
  adminApi.get("/admin/monitoring/recent-activity", { params: { limit } }).then((r) => r.data);

export const adminSendResetLink = (userId) =>
  adminApi.post(`/admin/monitoring/users/${userId}/reset-password`).then((r) => r.data);

export const adminToggleUserActive = (userId, isActive) =>
  adminApi
    .put(`/admin/monitoring/users/${userId}/active`, { is_active: !!isActive })
    .then((r) => r.data);

// ---------- Backups ----------
export const listBackups = (limit = 60) =>
  adminApi.get("/admin/backups", { params: { limit } }).then((r) => r.data.backups);

export const runBackup = () =>
  adminApi.post("/admin/backups/run", null, { timeout: 180000 }).then((r) => r.data);

export const backupDownloadUrl = (backupId) =>
  adminApi.get(`/admin/backups/${backupId}/download`).then((r) => r.data);

export const deleteBackup = (backupId) =>
  adminApi.delete(`/admin/backups/${backupId}`).then((r) => r.data);

export const restoreBackup = (backupId, confirm) =>
  adminApi
    .post(`/admin/backups/${backupId}/restore`, { confirm }, { timeout: 30 * 60 * 1000 })
    .then((r) => r.data);

// Disaster-recovery — upload a .gz archive from the operator's machine and
// restore from it. Long timeout because the file may be large and
// mongorestore can run for several minutes.
export const uploadRestoreBackup = (file, confirm, onUploadProgress) => {
  const fd = new FormData();
  fd.append("confirm", confirm);
  fd.append("file", file);
  return adminApi
    .post("/admin/backups/upload-restore", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 60 * 60 * 1000,
      onUploadProgress,
    })
    .then((r) => r.data);
};

export const listRestoreLogs = (limit = 30) =>
  adminApi.get("/admin/backups/restore-logs", { params: { limit } }).then((r) => r.data.restores);

// Server-side required confirmation phrase — plain Arabic (no tashkeel) so
// any keyboard / copy-paste path matches.
export const RESTORE_CONFIRMATION = "نعم استرجع";

// Tolerant comparison: strip Arabic diacritics + collapse whitespace so users
// who paste the phrase from a tashkeel-rendered source still pass the gate.
const _TASHKEEL = /[\u064B-\u065F\u0670]/g;
export const normalizeArabic = (s) =>
  (s || "").replace(_TASHKEEL, "").replace(/\s+/g, " ").trim();

export const isRestoreConfirmed = (input) =>
  normalizeArabic(input) === normalizeArabic(RESTORE_CONFIRMATION);

// ---------- Bar Card Anti-Impersonation ----------
export const adminBarCardDuplicates = () =>
  adminApi.get("/admin/lawyers/bar-card-duplicates").then((r) => r.data);

// ---------- Chat Archive (S3 storage tier for sessions > 90 days) ----------
export const chatArchiveStats = () =>
  adminApi.get("/admin/chat-archive/stats").then((r) => r.data);

export const runChatArchive = (max = 1000) =>
  adminApi
    .post("/admin/chat-archive/run", null, {
      params: { max_sessions: max },
      timeout: 5 * 60 * 1000, // 5 min — sweep can be long on first run
    })
    .then((r) => r.data);

// ---------- Admin user management (super_admin only) ----------
export const listAdmins = () =>
  adminApi.get("/admin/admins").then((r) => r.data.admins);

export const createAdmin = (data) =>
  adminApi.post("/admin/admins", data).then((r) => r.data);

export const updateAdmin = (id, data) =>
  adminApi.put(`/admin/admins/${id}`, data).then((r) => r.data);

export const deleteAdmin = (id) =>
  adminApi.delete(`/admin/admins/${id}`).then((r) => r.data);

// ---------- Cassation (write ops require role) ----------
export const adminCreateCassation = (data, opts = {}) => {
  const params = opts.force ? { force: true } : {};
  return adminApi.post("/cassation", data, { params }).then((r) => r.data);
};

export const adminUpdateCassation = (id, data, opts = {}) => {
  const params = opts.force ? { force: true } : {};
  return adminApi.put(`/cassation/${id}`, data, { params }).then((r) => r.data);
};

export const adminDeleteCassation = (id) =>
  adminApi.delete(`/cassation/${id}`).then((r) => r.data);

// ---------- Bulk Cassation Import (admin/cassation/bulk-import) ----------
// Long timeout: Claude Vision can take 30-120s for multi-page PDFs.
const BULK_TIMEOUT = 180000;  // 3 minutes

export const bulkImportSingle = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return adminApi
    .post("/admin/cassation/bulk-import", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: BULK_TIMEOUT,
    })
    .then((r) => r.data);
};

export const bulkImportZip = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return adminApi
    .post("/admin/cassation/bulk-import/zip", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: BULK_TIMEOUT,
    })
    .then((r) => r.data);
};

export const bulkJobs = (limit = 20) =>
  adminApi
    .get("/admin/cassation/bulk-import/jobs", { params: { limit } })
    .then((r) => r.data.jobs);

export const bulkJobStatus = (jobId) =>
  adminApi.get(`/admin/cassation/bulk-import/jobs/${jobId}`).then((r) => r.data);

export const cassationReviewQueue = (limit = 50) =>
  adminApi.get("/admin/cassation/review-queue", { params: { limit } })
    .then((r) => r.data);

// ---------- SJC.IQ auto-import ----------
export const sjcStats = () =>
  adminApi.get("/admin/sjc/stats").then((r) => r.data);

export const sjcJobs = (limit = 20) =>
  adminApi.get("/admin/sjc/jobs", { params: { limit } }).then((r) => r.data.jobs);

export const sjcJobStatus = (jobId) =>
  adminApi.get(`/admin/sjc/jobs/${jobId}`).then((r) => r.data);

export const sjcStartScrape = (start_id, end_id, concurrency = 4) =>
  adminApi
    .post("/admin/sjc/scrape", { start_id, end_id, concurrency })
    .then((r) => r.data);

export const sjcDailyCheck = () =>
  adminApi.post("/admin/sjc/daily-check").then((r) => r.data);

// ---------- Helpers ----------
export const ROLE_LABELS = {
  reader: "قارئ",
  uploader: "رافع",
  editor: "محرّر",
  full_admin: "مدير",
  super_admin: "مدير عام",
};

export const ROLE_LEVELS = {
  reader: 1,
  uploader: 2,
  editor: 3,
  full_admin: 4,
  super_admin: 5,
};

export const hasRole = (admin, min) =>
  !!admin && ROLE_LEVELS[admin.role] >= ROLE_LEVELS[min];

export const formatApiError = (err) => {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "خطأ غير متوقع";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" — ");
  return String(d);
};

// ---------- Broadcasts ----------
export const listBroadcasts = (limit = 50) =>
  adminApi.get("/admin/broadcasts", { params: { limit } }).then((r) => r.data);

export const previewBroadcastCount = (audience = "all") =>
  adminApi
    .get("/admin/broadcasts/preview-count", { params: { audience } })
    .then((r) => r.data);

export const sendBroadcast = (payload) =>
  adminApi.post("/admin/broadcasts", payload).then((r) => r.data);

export const flushCassationDigest = () =>
  adminApi.post("/admin/cassation-digest/flush-now").then((r) => r.data);

// ---------- Law Articles CRUD (editor+) ----------
export const listLawArticles = (lawId, q) =>
  adminApi
    .get(`/admin/laws/${lawId}/articles`, { params: q ? { q } : {} })
    .then((r) => r.data);

export const lawArticleGaps = (lawId) =>
  adminApi.get(`/admin/laws/${lawId}/gaps`).then((r) => r.data);

export const createLawArticle = (lawId, payload) =>
  adminApi.post(`/admin/laws/${lawId}/articles`, payload).then((r) => r.data);

export const updateLawArticle = (articleId, payload) =>
  adminApi.put(`/admin/laws/articles/${articleId}`, payload).then((r) => r.data);

export const deleteLawArticle = (articleId) =>
  adminApi.delete(`/admin/laws/articles/${articleId}`).then((r) => r.data);

// ---------- Amendments lifecycle ----------
export const addArticleAmendment = (articleId, payload) =>
  adminApi.post(`/admin/laws/articles/${articleId}/amendments`, payload).then((r) => r.data);

export const removeArticleAmendment = (articleId, amendmentId) =>
  adminApi.delete(`/admin/laws/articles/${articleId}/amendments/${amendmentId}`).then((r) => r.data);

export const repealArticle = (articleId, payload) =>
  adminApi.post(`/admin/laws/articles/${articleId}/repeal`, payload).then((r) => r.data);

export const unrepealArticle = (articleId) =>
  adminApi.post(`/admin/laws/articles/${articleId}/unrepeal`).then((r) => r.data);

// ---------- AI Amendment Analyzer ----------
export const analyzeAmendmentText = (lawId, text) =>
  adminApi
    .post(`/admin/laws/${lawId}/analyze-amendment`, { text }, { timeout: 90000 })
    .then((r) => r.data);

// ---------- Custom laws CRUD (editor+) ----------
export const createCustomLaw = (payload) =>
  adminApi.post("/admin/laws/custom", payload).then((r) => r.data);

export const deleteCustomLaw = (lawId) =>
  adminApi.delete(`/admin/laws/custom/${lawId}`).then((r) => r.data);

// ---------- Bulk delete all articles for a law ----------
export const bulkDeleteLawArticles = (lawId, { includeManual = false } = {}) =>
  adminApi
    .delete(`/admin/laws/${lawId}/articles`, {
      params: { include_manual: includeManual ? "true" : "false" },
    })
    .then((r) => r.data);


// ---------- Internal Regulations (الأنظمة الداخلية) ----------
// Parallel surface to the laws CRUD above. Same UX, separate collections.
// See `/app/backend/routers/admin_internal_regulations.py` for the contract.
export const createCustomRegulation = (payload) =>
  adminApi.post("/admin/regulations/custom", payload).then((r) => r.data);

export const listAdminRegulations = () =>
  adminApi.get("/admin/regulations/custom").then((r) => r.data.regulations || []);

export const deleteCustomRegulation = (regulationId) =>
  adminApi.delete(`/admin/regulations/custom/${regulationId}`).then((r) => r.data);

export const regulationsScrapedStats = () =>
  adminApi.get("/admin/regulations/scraped-stats").then((r) => r.data);

// ---------- Regulation Articles CRUD (mirrors law-articles surface) ----------
export const listRegulationArticles = (regulationId, q) =>
  adminApi
    .get(`/admin/regulations/${regulationId}/articles`, { params: q ? { q } : {} })
    .then((r) => r.data);

export const regulationArticleGaps = (regulationId) =>
  adminApi.get(`/admin/regulations/${regulationId}/gaps`).then((r) => r.data);

export const createRegulationArticle = (regulationId, payload) =>
  adminApi.post(`/admin/regulations/${regulationId}/articles`, payload).then((r) => r.data);

export const updateRegulationArticle = (articleId, payload) =>
  adminApi.put(`/admin/regulations/articles/${articleId}`, payload).then((r) => r.data);

export const deleteRegulationArticle = (articleId) =>
  adminApi.delete(`/admin/regulations/articles/${articleId}`).then((r) => r.data);

export const bulkDeleteRegulationArticles = (regulationId, { includeManual = false } = {}) =>
  adminApi
    .delete(`/admin/regulations/${regulationId}/articles`, {
      params: { include_manual: includeManual ? "true" : "false" },
    })
    .then((r) => r.data);

export const repurifyRegulation = (regulationId, mode) =>
  adminApi
    .post(
      "/admin/regulations/repurify",
      { regulation_id: regulationId, mode },
      { timeout: 600000 },
    )
    .then((r) => r.data);

// ---------- Health Center (added 2026-02) ----------
export const healthCenterSystem = () => adminApi.get("/admin/health-center/system").then((r) => r.data);
export const healthCenterIntegrations = () => adminApi.get("/admin/health-center/integrations").then((r) => r.data);
export const healthCenterCodeHealth = () => adminApi.get("/admin/health-center/code-health").then((r) => r.data);
export const healthCenterFiles = () => adminApi.get("/admin/health-center/files").then((r) => r.data);
export const sentryTestBackend = (kind = "exception") =>
  adminApi.post(`/admin/health-center/sentry-test?kind=${kind}`).then((r) => r.data);
export const healthCenterRagStats = () => adminApi.get("/admin/health-center/rag-stats").then((r) => r.data);
export const healthCenterRagProbe = (query) => adminApi.post(`/admin/health-center/rag-stats/probe?query=${encodeURIComponent(query)}`).then((r) => r.data);
export const healthCenterLlmUsage = (days = 7) => adminApi.get(`/admin/health-center/llm-usage?days=${days}`).then((r) => r.data);
export const auditLogsList = (params = {}) => adminApi.get("/admin/audit-logs", { params }).then((r) => r.data);
export const auditLogsActions = () => adminApi.get("/admin/audit-logs/actions").then((r) => r.data);
